import { useState, useMemo } from 'react';
import {
  Box,
  CircularProgress,
  Alert,
  Button,
  Select,
  MenuItem,
  Chip,
  Typography,
} from '@mui/material';
import DomainIcon from '@mui/icons-material/Domain';
import CloseIcon from '@mui/icons-material/Close';
import type { VariationStatus } from '@billda/api';
import { PageHeader } from '../../../shared/components/layout/PageHeader';
import { PageToolbar } from '../../../shared/components/layout/PageToolbar';
import { PageContentArea } from '../../../shared/components/layout/PageContentArea';
import { SidePanel, ResizableHandle } from '../../../shared/components/panel';
import { ledgerContainerSx } from '../../../shared/components/ledger';
import { VariationDetailsPanel } from '../components/panel/organisms/VariationDetailsPanel';
import { useVariationsList } from '../hooks/useVariationsList';
import { useSitesSummary } from '../hooks/useSitesSummary';
import { useSitePanelState } from '../hooks/useSitePanelState';
import { useVariationsUIStore } from '../stores/variationsUIStore';
import { ChipTabs } from '../../../shared/components/ui/ChipTabs';
import { VariationsLedger } from '../variations-ledger/VariationsLedger';
import { VARIATIONS_COLLAPSE } from '../variations-ledger/columns';
import {
  toVariationRows,
  type VariationLedgerRowModel,
} from '../variations-ledger/toVariationRows';

const STATUS_TABS = [
  { label: 'All', value: '' },
  { label: 'Booked', value: 'Booked' },
  { label: 'Approved', value: 'Approved' },
  { label: 'Rejected', value: 'Rejected' },
] as const;

const VariationsPage = () => {
  const { siteIdFilter, setSiteIdFilter, statusFilter, setStatusFilter } =
    useVariationsUIStore();
  const {
    isOpen,
    id: panelVariationId,
    type,
    closePanel,
    openPanel,
  } = useSitePanelState();
  const { data: sites } = useSitesSummary();
  // Track only what the user has explicitly collapsed — so every week is
  // expanded by default, including weeks arriving with the next page.
  const [collapsedWeeks, setCollapsedWeeks] = useState<ReadonlySet<string>>(
    new Set()
  );

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
  } = useVariationsList(siteIdFilter, statusFilter);

  const allItems = useMemo(
    () => data?.pages.flatMap(p => p.items) ?? [],
    [data]
  );
  const totalCount = data?.pages[0]?.totalCount ?? 0;
  const weeks = useMemo(() => toVariationRows(allItems), [allItems]);

  const toggleWeek = (key: string) =>
    setCollapsedWeeks(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });

  const handleRowClick = (row: VariationLedgerRowModel) => {
    openPanel(
      'variation',
      row.id,
      row.siteId.toString(),
      row.plotId.toString()
    );
  };

  const handleSiteFilterChange = (value: string) => {
    setSiteIdFilter(value ? parseInt(value, 10) : undefined);
  };

  const selectedSiteName = siteIdFilter
    ? sites?.find(s => s.id === siteIdFilter)?.name
    : undefined;

  const isFilterActive = !!siteIdFilter;

  const filterContent = (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontWeight: 500, display: 'block', mb: 0.75 }}
      >
        Site
      </Typography>
      <Select
        fullWidth
        size="small"
        value={siteIdFilter?.toString() ?? ''}
        onChange={e => handleSiteFilterChange(e.target.value as string)}
        displayEmpty
        inputProps={{ 'data-testid': 'site-filter-select' }}
      >
        <MenuItem value="">All sites</MenuItem>
        {sites?.map(site => (
          <MenuItem key={site.id} value={site.id.toString()}>
            {site.name}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );

  const filterChips =
    siteIdFilter && selectedSiteName ? (
      <Chip
        size="small"
        icon={<DomainIcon sx={{ fontSize: '14px !important' }} />}
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 500 }}>
              Site
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mx: 0.25 }}
            >
              is
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 500 }}>
              {selectedSiteName}
            </Typography>
          </Box>
        }
        onDelete={() => setSiteIdFilter(undefined)}
        deleteIcon={<CloseIcon sx={{ fontSize: '14px !important' }} />}
        sx={{ height: 26, borderRadius: 1 }}
      />
    ) : undefined;

  const statusTabs = (
    <ChipTabs
      tabs={[...STATUS_TABS]}
      value={statusFilter ?? ''}
      onChange={v => setStatusFilter((v as VariationStatus) || undefined)}
      data-testid="variations-status-tabs"
    />
  );

  const panelContent = (
    <>
      {isOpen && type === 'variation' && panelVariationId && (
        <>
          <ResizableHandle />
          <SidePanel open={isOpen} onClose={closePanel} title="Variation">
            <VariationDetailsPanel variationId={panelVariationId} />
          </SidePanel>
        </>
      )}
    </>
  );

  const noItems = allItems.length === 0 && !isLoading;
  const hasFilters = isFilterActive || !!statusFilter;

  return (
    <PageContentArea isPanelOpen={isOpen} panel={panelContent}>
      <PageHeader title="Variations" />
      <PageToolbar
        tabs={statusTabs}
        filterContent={filterContent}
        filterChips={filterChips}
        isFilterActive={isFilterActive}
      />

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          ...ledgerContainerSx(VARIATIONS_COLLAPSE.container),
        }}
      >
        {isLoading && !data ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 200,
              p: 3,
            }}
          >
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ m: 2 }}>
            {(error as Error).message}
          </Alert>
        ) : noItems && !hasFilters ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              No variations yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Variations booked from the mobile app will appear here.
            </Typography>
          </Box>
        ) : noItems ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              No variations match these filters
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Try different filters or clear them to see all variations.
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setSiteIdFilter(undefined);
                setStatusFilter(undefined);
              }}
            >
              Clear filters
            </Button>
          </Box>
        ) : (
          <VariationsLedger
            weeks={weeks}
            collapsedWeeks={collapsedWeeks}
            onToggleWeek={toggleWeek}
            onOpenRow={handleRowClick}
            totalCount={totalCount}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            onLoadMore={() => fetchNextPage()}
          />
        )}
      </Box>
    </PageContentArea>
  );
};

export default VariationsPage;
